import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { GuestRatingSummary } from '@apps/main/new-desktop/modules/customersRating';
import type {
  HotelIngredient,
  RecipeMainIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';

import {
  isAvailableFerryIngredientResponse,
  isAvailableFlightIngredientResponse,
} from '@libs/search-service-smart-search';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import type {
  CarrierStaticInfo,
  ContentApiImage,
  DestinationStaticInfo,
  GuestRatingBreakdown,
  HotelBookableOptionWithPriceBreakdown,
  HotelIngredientResponse,
  OverviewDestinationStaticInfo,
  PortStaticInfo,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';

import type {
  PeekPriceHotelCardData,
  PeekPriceMediumBookableTransportCardData,
  PeekPriceModalFerryPlusHotelItinerary,
  PeekPriceModalFlightPlusHotelItinerary,
  PeekPriceModalHotelOnlyItinerary,
  PeekPriceRoomDetails,
} from '../../models';
import { PeekPriceModalItineraryType } from '../../models';

const { t } = i18n.global;

export function calculateDates(
  displayDate: Date | undefined,
  totalNights: number
): string | undefined {
  if (!displayDate) {
    return;
  }

  const startDate = new SemboDate(displayDate);
  const endDate = new SemboDate(displayDate).add({
    days: totalNights,
  });

  const sameMonth =
    startDate.format(DateFormat.MonthFullName) ===
    endDate.format(DateFormat.MonthFullName);

  return sameMonth
    ? `${startDate.format(DateFormat.Day)} - ${endDate.format(
        DateFormat.Day
      )} ${endDate.format(DateFormat.FullMonthAndYear)}`
    : `${startDate.format(DateFormat.Day)} ${startDate.format(
        DateFormat.MonthFullName
      )} - ${endDate.format(DateFormat.Day)} ${endDate.format(
        DateFormat.FullMonthAndYear
      )}`;
}

export function getHotelSelectionCardSubtitle(
  product: DestinationStaticInfo | OverviewDestinationStaticInfo
): string {
  if (product.mainPolygon) {
    return product.mainPolygon.fullName;
  }

  if (product?.address?.city) {
    return product.address.country
      ? `${product.address.city}, ${product.address.country}`
      : product.address.city;
  }

  return '';
}

// NOTE: HOTEL ONLY

interface PeekPriceModalHotelOnlyItineraryParams {
  displayDate: Date | undefined;
  paxes: string;
  hotelAvail: HotelIngredientResponse | undefined;
  hotelStaticInfo: OverviewDestinationStaticInfo | DestinationStaticInfo;
  getImageUrl: (file: ContentApiImage) => string;
  formatMoney: (money: number) => string;
  getGuestRating: (
    guestRatings: GuestRatingBreakdown | undefined
  ) => GuestRatingSummary | undefined;
}

export function getPeekPriceModalHotelOnlyItinerary(
  params: PeekPriceModalHotelOnlyItineraryParams
): PeekPriceModalHotelOnlyItinerary {
  const imagesLimit = 3;
  const amount = params.hotelAvail?.suggested?.totalPrice.amount;
  const price = amount ? params.formatMoney(amount) : undefined;
  const rooms: PeekPriceRoomDetails[] | undefined =
    params?.hotelAvail?.suggested?.rooms.map(
      (room: HotelBookableOptionWithPriceBreakdown) => ({
        description: room.description,
        boardBasisCode: room.boardBasis,
      })
    );
  const numberOfNights = params.hotelAvail?.numberOfNights;
  const dates = numberOfNights
    ? calculateDates(params.displayDate, numberOfNights)
    : '';
  const hotel: PeekPriceHotelCardData = {
    dates,
    imageUrls: params.hotelStaticInfo.images
      .slice(0, imagesLimit)
      .map((image: ContentApiImage) => params.getImageUrl(image)),
    nights: numberOfNights
      ? `${numberOfNights} ${t('planner.nights', numberOfNights)}`
      : '',
    hotelName: params.hotelStaticInfo.name,
    hotelDestination: getHotelSelectionCardSubtitle(params.hotelStaticInfo),
    hotelStars: params.hotelStaticInfo.semboSunRating,
    guestRating: params.getGuestRating(params.hotelStaticInfo.guestRatings),
    rooms,
  };

  const hotelOnlyItinerary: PeekPriceModalHotelOnlyItinerary = {
    type: PeekPriceModalItineraryType.HotelOnly,
    dates,
    price,
    paxes: params.paxes,
    hotel,
  };

  return hotelOnlyItinerary;
}

// NOTE: FLIGHT PLUS HOTEL
// NOTE: FERRY PLUS HOTEL
// NOTE: BOOKABLE TRANSPORT PLUS HOTEL
export function getFlightDescriptionFromNumberOfStops(
  numberOfStops: number
): string {
  return numberOfStops === 0
    ? t('planner.direct_flight')
    : t('planner.number_of_stops', [numberOfStops], numberOfStops);
}

interface GetUnavailableBookableTransportPlusHotelItineraryParams {
  type: 'flight' | 'ferry';
  hotelIngredient: HotelIngredient;
  hotelStaticInfo: OverviewDestinationStaticInfo | DestinationStaticInfo;
  paxes: string;
  imagesLimit: number;
  getImageUrl: (file: ContentApiImage) => string;
  getGuestRating: (
    guestRatings: GuestRatingBreakdown | undefined
  ) => GuestRatingSummary | undefined;
}

export function getUnavailableBookableTransportPlusHotelItinerary(
  params: GetUnavailableBookableTransportPlusHotelItineraryParams
):
  | PeekPriceModalFlightPlusHotelItinerary
  | PeekPriceModalFerryPlusHotelItinerary {
  const numberOfNights = params.hotelIngredient.numberOfNights;
  const notAvailHotel: PeekPriceHotelCardData = {
    dates: undefined,
    imageUrls: params.hotelStaticInfo.images
      .slice(0, params.imagesLimit)
      .map((image: ContentApiImage) => params.getImageUrl(image)),
    nights: numberOfNights
      ? `${numberOfNights} ${t('planner.nights', numberOfNights)}`
      : '',
    hotelName: params.hotelStaticInfo.name,
    hotelDestination: getHotelSelectionCardSubtitle(params.hotelStaticInfo),
    hotelStars: params.hotelStaticInfo.semboSunRating,
    guestRating: params.getGuestRating(params.hotelStaticInfo.guestRatings),
    rooms: undefined,
  };

  const notAvailDepartureTransport: PeekPriceMediumBookableTransportCardData = {
    date: undefined,
    direction: t('filter.flightFilters.departure'),
    stops: undefined,
    carrierLogoUrl: undefined,
    carrierLogoAltText:
      params.type === 'flight'
        ? t('a11y.flight_carrier_logo')
        : t('a11y.ferry_carrier_logo'),
    altLogoIcon: params.type === 'flight' ? 'airplane' : 'ferry',
    time: undefined,
    ports: undefined,
  };

  const notAvailArrivalTransport: PeekPriceMediumBookableTransportCardData = {
    date: undefined,
    direction: t('filter.flightFilters.arrival'),
    stops: undefined,
    carrierLogoUrl: undefined,
    carrierLogoAltText:
      params.type === 'flight'
        ? t('a11y.flight_carrier_logo')
        : t('a11y.ferry_carrier_logo'),
    altLogoIcon: params.type === 'flight' ? 'airplane' : 'ferry',
    time: undefined,
    ports: undefined,
  };

  const notAvailableRecipe: PeekPriceModalFlightPlusHotelItinerary = {
    type: PeekPriceModalItineraryType.FlightPlusHotel,
    tripDeparture: undefined,
    dates: undefined,
    price: undefined,
    paxes: params.paxes,
    departureTransport: notAvailDepartureTransport,
    hotel: notAvailHotel,
    arrivalTransport: notAvailArrivalTransport,
  };

  return notAvailableRecipe;
}

interface PeekPriceModalBookableTransportPlusHotelItineraryParams {
  peekPriceRecipe: RecipeMainIngredient[];
  selectedItinerary: DateWithItinerary | undefined;
  hotelStaticInfo: OverviewDestinationStaticInfo | DestinationStaticInfo;
  paxes: string;
  displayDate: Date | undefined;
  type:
    | PeekPriceModalItineraryType.FlightPlusHotel
    | PeekPriceModalItineraryType.FerryPlusHotel;
  getImageUrl: (file: ContentApiImage) => string;
  formatMoney: (money: number) => string;
  getGuestRating: (
    guestRatings: GuestRatingBreakdown | undefined
  ) => GuestRatingSummary | undefined;
  getPortStaticInfo: (
    code: string,
    kind: IngredientType
  ) => PortStaticInfo | undefined;
  getCarriersStaticInfo: (
    carriers: string[]
  ) => Record<string, CarrierStaticInfo>;
}

export function getPeekPriceModalBookableTransportPlusHotelItinerary(
  params: PeekPriceModalBookableTransportPlusHotelItineraryParams
):
  | PeekPriceModalFlightPlusHotelItinerary
  | PeekPriceModalFerryPlusHotelItinerary {
  const imagesLimit = 3;
  const hotelIngredient = params.peekPriceRecipe[1] as HotelIngredient;
  const numberOfNights = hotelIngredient.numberOfNights;
  const isFlight = params.type === PeekPriceModalItineraryType.FlightPlusHotel;
  const ingredientType = isFlight
    ? IngredientType.Flight
    : IngredientType.Ferry;
  const notAvailableRecipe = getUnavailableBookableTransportPlusHotelItinerary({
    type: isFlight ? 'flight' : 'ferry',
    hotelIngredient,
    hotelStaticInfo: params.hotelStaticInfo,
    paxes: params.paxes,
    imagesLimit: 3,
    getImageUrl: params.getImageUrl,
    getGuestRating: params.getGuestRating,
  });

  const departureIngredient = isFlight
    ? params.selectedItinerary?.itinerary?.flightOnlyItinerary[0]
    : params.selectedItinerary?.itinerary?.ferryOnlyItinerary[0];
  const arrivalIngredient = isFlight
    ? params.selectedItinerary?.itinerary?.flightOnlyItinerary[1]
    : params.selectedItinerary?.itinerary?.ferryOnlyItinerary[1];

  const typeGuard = isFlight
    ? isAvailableFlightIngredientResponse
    : isAvailableFerryIngredientResponse;

  if (
    !departureIngredient ||
    !arrivalIngredient ||
    !typeGuard(departureIngredient) ||
    !typeGuard(arrivalIngredient)
  ) {
    return notAvailableRecipe;
  }

  const departureTime = `${new SemboDate(
    departureIngredient.departureDate
  ).format(DateFormat.Time)} - ${new SemboDate(
    departureIngredient.arrivalDate
  ).format(DateFormat.Time)}`;
  const arrivalTime = `${new SemboDate(arrivalIngredient.departureDate).format(
    DateFormat.Time
  )} - ${new SemboDate(arrivalIngredient.arrivalDate).format(DateFormat.Time)}`;

  const firstTransportDeparturePort = params.getPortStaticInfo(
    departureIngredient.departureIata,
    ingredientType
  );

  const firstTransportArrivalPort = params.getPortStaticInfo(
    departureIngredient.arrivalIata,
    ingredientType
  );

  const secondTransportDeparturePort = params.getPortStaticInfo(
    arrivalIngredient.departureIata,
    ingredientType
  );

  const secondTransportArrivalPort = params.getPortStaticInfo(
    arrivalIngredient.arrivalIata,
    ingredientType
  );

  if (
    !firstTransportDeparturePort ||
    !firstTransportArrivalPort ||
    !secondTransportDeparturePort ||
    !secondTransportArrivalPort
  ) {
    return notAvailableRecipe;
  }

  const departureCarrier =
    departureIngredient.platingCarrier ?? departureIngredient.carriers[0];
  const arrivalCarrier =
    arrivalIngredient.platingCarrier ?? arrivalIngredient.carriers[0];

  if (!departureCarrier || !arrivalCarrier) {
    return notAvailableRecipe;
  }

  const carrierStaticInfoDict = params.getCarriersStaticInfo([
    departureCarrier,
    arrivalCarrier,
  ]);

  const departureTransport: PeekPriceMediumBookableTransportCardData = {
    date: new SemboDate(departureIngredient.departureDate).format(
      DateFormat.DateMonth
    ),
    direction: t('filter.flightFilters.departure'),
    stops:
      'numberOfStops' in departureIngredient
        ? getFlightDescriptionFromNumberOfStops(
            departureIngredient.numberOfStops
          )
        : t('planner.direct_flight_short'),
    carrierLogoUrl: carrierStaticInfoDict[departureCarrier]?.logo,
    carrierLogoAltText: t('a11y.flight_carrier_logo'),
    altLogoIcon: isFlight ? 'airplane' : 'ferry',
    time: departureTime,
    ports: `${firstTransportDeparturePort.position.name} (${firstTransportDeparturePort.code}) - ${firstTransportArrivalPort.position.name} (${firstTransportArrivalPort.code})`,
  };

  const arrivalTransport: PeekPriceMediumBookableTransportCardData = {
    date: new SemboDate(arrivalIngredient.departureDate).format(
      DateFormat.DateMonth
    ),
    direction: t('filter.flightFilters.arrival'),
    stops:
      'numberOfStops' in arrivalIngredient
        ? getFlightDescriptionFromNumberOfStops(arrivalIngredient.numberOfStops)
        : t('planner.direct_flight_short'),
    carrierLogoUrl: carrierStaticInfoDict[arrivalCarrier]?.logo,
    carrierLogoAltText: t('a11y.flight_carrier_logo'),
    altLogoIcon: isFlight ? 'airplane' : 'ferry',
    time: arrivalTime,
    ports: `${secondTransportDeparturePort.position.name} (${secondTransportDeparturePort.code}) - ${secondTransportArrivalPort.position.name} (${secondTransportArrivalPort.code})`,
  };

  const totalPrice = params.selectedItinerary?.itinerary?.totalPrice;
  const price = totalPrice ? params.formatMoney(totalPrice.amount) : undefined;

  const rooms: PeekPriceRoomDetails[] | undefined =
    params.selectedItinerary?.itinerary?.hotelOnlyItinerary[0]?.suggested?.rooms.map(
      (room: HotelBookableOptionWithPriceBreakdown) => ({
        description: room.description,
        boardBasisCode: room.boardBasis,
      })
    );
  const dates = numberOfNights
    ? calculateDates(params.displayDate, numberOfNights)
    : '';
  const hotel: PeekPriceHotelCardData = {
    dates,
    imageUrls: params.hotelStaticInfo.images
      .slice(0, imagesLimit)
      .map((image: ContentApiImage) => params.getImageUrl(image)),
    nights: numberOfNights
      ? `${numberOfNights} ${t('planner.nights', numberOfNights)}`
      : '',
    hotelName: params.hotelStaticInfo.name,
    hotelDestination: getHotelSelectionCardSubtitle(params.hotelStaticInfo),
    hotelStars: params.hotelStaticInfo.semboSunRating,
    guestRating: params.getGuestRating(params.hotelStaticInfo.guestRatings),
    rooms,
  };

  const bookableTransportPlusHotelRecipe:
    | PeekPriceModalFlightPlusHotelItinerary
    | PeekPriceModalFerryPlusHotelItinerary = {
    type: params.type,
    tripDeparture: new SemboDate(departureIngredient.departureDate).format(
      DateFormat.DateMonth
    ),
    dates,
    price,
    paxes: params.paxes,
    departureTransport,
    hotel,
    arrivalTransport,
  };

  return bookableTransportPlusHotelRecipe;
}
